import React, { useState } from 'react'
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Download, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import AdminSidePanel from './AdminSidePanel';
import { useSearchParams } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// Shared building blocks for the admin Analytics section (design 19a / 19d):
// cream page shell, view switch, range bar, metric cards, activity chart,
// bar lists, sortable table, pagination and export menu.

export const C = {
  navy: '#14213d',
  brass: '#8a6231',
  brassHover: '#6d4c25',
  gold: '#c9a26a',
  cream: '#f7f4ed',
  paper: '#fffdf8',
  green: '#3a7d4f',
  red: '#9a3b2e',
};

// ---------- ?view= URL param (analytics | list) ----------
export function useViewParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'list' ? 'list' : 'analytics';
  const setView = (v) => setSearchParams({ view: v });
  return [view, setView];
}

// ---------- page shell ----------
export function AdminPage({ setActivePanel, activePanel, title, subtitle, children }) {
  return (
    <div className='w-full min-h-full font-plexsans' style={{ background: C.cream }}>
      <AdminSidePanel setActivePanel={setActivePanel} activePanel={activePanel} />
      <div className='px-4 py-6 lg:px-10 lg:py-[34px]'>
        {title &&
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ margin: 0, font: "400 20px/1.3 'Spectral',serif", color: C.navy }}>{title}</h2>
            {subtitle && <div style={{ marginTop: '4px', fontSize: '13px', color: 'rgba(20,33,61,.5)' }}>{subtitle}</div>}
          </div>
        }
        {children}
      </div>
    </div>
  );
}

// ---------- segmented view switch (navy active segment) ----------
export function ViewSwitch({ options, value, onChange }) {
  return (
    <div className='inline-flex font-plexsans flex-wrap' style={{ background: 'rgba(20,33,61,.05)', borderRadius: '5px', padding: '3px' }}>
      {options.map((o) => {
        const active = value === o.key;
        return (
          <div
            key={o.key}
            onClick={() => onChange(o.key)}
            className='cursor-pointer whitespace-nowrap transition-colors'
            style={{
              padding: '8px 18px', fontSize: '12.5px', lineHeight: 1, borderRadius: '4px',
              fontWeight: active ? 600 : 500,
              background: active ? C.navy : 'transparent',
              color: active ? C.paper : 'rgba(20,33,61,.55)',
            }}
          >
            {o.label}
          </div>
        );
      })}
    </div>
  );
}

// ---------- "Showing <range>" + range control ----------
export const rangeLabelOf = (dateRange) => {
  const s = dateRange?.[0]?.startDate;
  const e = dateRange?.[0]?.endDate;
  if (!s || !e) return 'all time';
  if (moment(s).isSame(moment(e), 'date')) return moment(s).format('MMM D, YYYY');
  return moment(s).format('MMM D') + ' – ' + moment(e).format('MMM D, YYYY');
};

export function RangeBar({ dateRange, children }) {
  return (
    <div className='flex flex-wrap items-center justify-between gap-3' style={{ marginBottom: '24px' }}>
      <div style={{ fontSize: '12.5px', color: 'rgba(20,33,61,.5)' }}>
        Showing <span style={{ color: C.navy, fontWeight: 500 }}>{rangeLabelOf(dateRange)}</span>
      </div>
      {children}
    </div>
  );
}

// ---------- metric cards ----------
export function MetricGrid({ cols = 3, children }) {
  const lg = cols === 4 ? 'lg:grid-cols-4' : cols === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3';
  return (
    <div className={'grid grid-cols-1 md:grid-cols-2 ' + lg} style={{ gap: '14px', marginBottom: '28px' }}>
      {children}
    </div>
  );
}

export function MetricCard({ icon, tile, title, value, desc }) {
  return (
    <div className='bg-white font-plexsans' style={{ border: '1px solid rgba(20,33,61,.1)', borderRadius: '6px', padding: '20px' }}>
      <div className='flex items-center' style={{ gap: '10px', marginBottom: '14px' }}>
        <div className='flex-none flex items-center justify-center' style={{ width: '30px', height: '30px', borderRadius: '6px', background: tile }}>
          {icon}
        </div>
        <div style={{ font: "600 12.5px/1.3 'IBM Plex Sans',sans-serif", color: C.navy }}>{title}</div>
      </div>
      <div style={{ font: "400 30px/1 'Spectral',serif", color: C.navy, marginBottom: desc ? '8px' : 0 }}>{value ?? 0}</div>
      {desc && <div style={{ fontSize: '12px', lineHeight: 1.5, color: 'rgba(20,33,61,.5)' }}>{desc}</div>}
    </div>
  );
}

// ---------- panel ----------
export function Panel({ title, right, children, padding = '26px', style }) {
  return (
    <div className='bg-white min-w-0' style={{ border: '1px solid rgba(20,33,61,.1)', borderRadius: '6px', padding, ...style }}>
      {(title || right) &&
        <div className='flex flex-wrap items-center justify-between gap-3' style={{ marginBottom: '18px' }}>
          <div style={{ font: "600 13.5px/1.3 'IBM Plex Sans',sans-serif", color: C.navy }}>{title}</div>
          {right}
        </div>
      }
      {children}
    </div>
  );
}

// ---------- activity line chart ----------
const LineChartIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgba(20,33,61,.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </svg>
);

export function EmptyState({ text, icon = true, height }) {
  return (
    <div className='flex flex-col items-center justify-center text-center' style={{ gap: '10px', height, padding: height ? 0 : '32px 12px' }}>
      {icon && <LineChartIcon />}
      <div style={{ fontSize: '13px', color: 'rgba(20,33,61,.4)' }}>{text}</div>
    </div>
  );
}

// series: [{ label, color, data: number[] }]
export function ActivityChart({ title = 'Activity over time', series, xAxisValues, rangeLabel, height = 220 }) {
  const hasData = series.some((s) => (s.data || []).some((v) => v > 0));
  const data = {
    labels: xAxisValues,
    datasets: series.map((s) => ({
      label: s.label,
      data: s.data || [],
      borderColor: s.color,
      backgroundColor: s.color,
      borderWidth: 2,
      pointRadius: (xAxisValues?.length || 0) > 31 ? 0 : 2.5,
      pointHoverRadius: 4,
      tension: 0.35,
      fill: false,
    })),
  };
  const tickFont = { family: "'IBM Plex Sans', sans-serif", size: 11 };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: C.navy,
        titleFont: { family: "'IBM Plex Sans', sans-serif", size: 11.5, weight: '600' },
        bodyFont: tickFont, padding: 10, boxWidth: 8, boxHeight: 8,
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { font: tickFont, color: 'rgba(20,33,61,.45)', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
      y: { beginAtZero: true, grid: { color: 'rgba(20,33,61,.06)' }, border: { display: false }, ticks: { font: tickFont, color: 'rgba(20,33,61,.45)', precision: 0 } },
    },
  };
  return (
    <Panel
      title={title}
      right={
        <div className='flex flex-wrap items-center' style={{ gap: '16px' }}>
          {series.map((s) => (
            <div key={s.label} className='flex items-center' style={{ gap: '6px', fontSize: '12px', color: 'rgba(20,33,61,.6)' }}>
              <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: s.color, display: 'inline-block' }}></span>
              {s.label}
            </div>
          ))}
        </div>
      }
    >
      <div style={{ height: height + 'px', borderTop: '1px solid rgba(20,33,61,.08)', borderBottom: '1px solid rgba(20,33,61,.08)', padding: hasData ? '12px 0' : 0 }}>
        {hasData ?
          <Line options={options} data={data} />
          :
          <EmptyState height='100%' text={'No activity recorded for ' + (rangeLabel || 'this range')} />
        }
      </div>
    </Panel>
  );
}

// ---------- horizontal bar list (replaces the old pie charts) ----------
// items: [{ label, value }]
export function BarList({ items, color = C.brass, emptyText = 'Nothing to show for this range', maxHeight }) {
  const sorted = [...(items || [])].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...sorted.map((i) => i.value));
  if (sorted.length === 0) return <EmptyState icon={false} text={emptyText} />;
  return (
    <div className='flex flex-col' style={{ gap: '14px', maxHeight, overflowY: maxHeight ? 'auto' : undefined, paddingRight: maxHeight ? '6px' : 0 }}>
      {sorted.map((i) => (
        <div key={i.label} className='flex items-center' style={{ gap: '12px' }}>
          <div className='truncate flex-none' title={i.label} style={{ width: '40%', maxWidth: '220px', font: "500 12.5px/1.3 'IBM Plex Sans',sans-serif", color: C.navy }}>{i.label}</div>
          <div className='flex-1' style={{ height: '9px', borderRadius: '5px', background: 'rgba(20,33,61,.07)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: Math.max(2, Math.round((i.value / max) * 100)) + '%', borderRadius: '5px', background: color === C.brass ? 'linear-gradient(90deg,#8a6231,#c9a26a)' : color }} />
          </div>
          <div className='flex-none' style={{ width: '44px', textAlign: 'right', font: "600 12.5px/1 'IBM Plex Mono',monospace", color: 'rgba(20,33,61,.55)' }}>{i.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- filter row ----------
export function FilterRow({ label, control, right }) {
  return (
    <div className='flex flex-wrap items-center justify-between' style={{ gap: '12px', marginBottom: '16px' }}>
      <div className='flex flex-wrap items-center' style={{ gap: '10px' }}>
        {label && <span style={{ font: "500 12.5px/1 'IBM Plex Sans',sans-serif", color: 'rgba(20,33,61,.6)' }}>{label}</span>}
        {control}
      </div>
      {right}
    </div>
  );
}

export function ResultCount({ from, to, total, noun = 'results' }) {
  return (
    <div style={{ fontSize: '12.5px', color: 'rgba(20,33,61,.55)', marginBottom: '12px' }}>
      Showing <span style={{ color: C.navy, fontWeight: 600 }}>{from}–{to}</span> of <span style={{ color: C.navy, fontWeight: 600 }}>{total}</span> {noun}
    </div>
  );
}

// ---------- export (CSV / Excel) ----------
const loadSheetJS = () => new Promise((resolve, reject) => {
  if (window.XLSX) return resolve(window.XLSX);
  const script = document.createElement('script');
  script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
  script.onload = () => (window.XLSX ? resolve(window.XLSX) : reject(new Error('XLSX not available')));
  script.onerror = reject;
  document.body.appendChild(script);
});

// rows: array of arrays, first row = headers
export async function downloadRows(rows, format, fileBase) {
  if (format === 'csv') {
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileBase + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } else {
    const XLSX = await loadSheetJS();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileBase + '.xlsx');
  }
}

export function ExportMenu({ onExport, exporting }) {
  const [open, setOpen] = useState(false);
  return (
    <div className='relative'>
      <button
        type='button'
        disabled={exporting}
        onClick={() => setOpen((o) => !o)}
        className='flex items-center transition-colors hover:bg-[rgba(138,98,49,0.06)] disabled:opacity-60'
        style={{ gap: '7px', border: '1.5px solid #8a6231', color: C.brass, font: "500 12.5px/1 'IBM Plex Sans',sans-serif", padding: '9px 14px', borderRadius: '5px', background: 'transparent' }}
      >
        <Download size={14} strokeWidth={2} />
        {exporting ? 'Exporting…' : 'Export'}
        <ChevronDown size={14} strokeWidth={2} />
      </button>
      {open &&
        <>
          <div className='fixed inset-0 z-30' onClick={() => setOpen(false)} />
          <div className='absolute right-0 z-40 bg-white overflow-hidden' style={{ top: '42px', minWidth: '170px', border: '1px solid rgba(20,33,61,.12)', borderRadius: '6px', boxShadow: '0 8px 24px rgba(20,33,61,.12)', padding: '4px 0' }}>
            {[{ key: 'csv', label: 'CSV' }, { key: 'xlsx', label: 'Excel (.xlsx)' }].map((o) => (
              <div key={o.key} onClick={() => { setOpen(false); onExport(o.key); }} className='cursor-pointer hover:bg-[rgba(138,98,49,0.06)]' style={{ padding: '10px 14px', fontSize: '13px', color: C.navy }}>
                {o.label}
              </div>
            ))}
          </div>
        </>
      }
    </div>
  );
}

// ---------- sortable data table ----------
// columns: [{ label, width, right, sortVal(row), render(row, index) }]
export function DataTable({ columns, rows, loading, emptyText = 'Nothing to show', emptyAction, onRowClick, minWidth = 780, footer }) {
  const [sort, setSort] = useState({ col: -1, dir: 'asc' });
  const grid = columns.map((c) => c.width || 'minmax(0,1fr)').join(' ');
  const toggle = (i) => {
    if (!columns[i].sortVal) return;
    setSort((p) => (p.col === i ? { col: i, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { col: i, dir: 'asc' }));
  };
  const list = sort.col < 0 || !columns[sort.col]?.sortVal ? rows : [...rows].sort((a, b) => {
    const va = columns[sort.col].sortVal(a);
    const vb = columns[sort.col].sortVal(b);
    const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va ?? '').localeCompare(String(vb ?? ''));
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className='bg-white' style={{ border: '1px solid rgba(20,33,61,.12)', borderRadius: '6px', overflow: 'hidden' }}>
      <div className='overflow-x-auto'>
        <div style={{ minWidth: minWidth + 'px' }}>
          <div className='grid' style={{ gridTemplateColumns: grid, background: C.navy }}>
            {columns.map((c, i) => {
              const active = sort.col === i;
              const Icon = active ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
              return (
                <div
                  key={c.label + i}
                  onClick={() => toggle(i)}
                  className={'flex items-center select-none ' + (c.sortVal ? 'cursor-pointer ' : '') + (c.right ? 'justify-end' : '')}
                  style={{ padding: '12px', gap: '4px', font: "600 10px/1.2 'IBM Plex Mono',monospace", textTransform: 'uppercase', letterSpacing: '.06em', color: active ? '#f7f4ed' : 'rgba(247,244,237,.75)' }}
                >
                  <span>{c.label}</span>
                  {c.sortVal && <Icon size={11} strokeWidth={2.2} color={active ? C.gold : 'rgba(247,244,237,.35)'} className='flex-none' />}
                </div>
              );
            })}
          </div>

          {loading ?
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className='grid items-center' style={{ gridTemplateColumns: grid, borderTop: '1px solid rgba(20,33,61,.07)', height: '44px', background: i % 2 ? '#fcfbf8' : '#fff' }}>
                {columns.map((c, j) => (
                  <div key={j} style={{ padding: '0 12px' }} className={c.right ? 'flex justify-end' : ''}>
                    <div className='animate-pulse rounded' style={{ height: '10px', width: '60%', background: 'rgba(20,33,61,.07)' }} />
                  </div>
                ))}
              </div>
            ))
          : list.length > 0 ?
            list.map((row, k) => (
              <div
                key={row._id || row._rowKey || k}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={'grid items-center transition-colors ' + (onRowClick ? 'cursor-pointer hover:!bg-[rgba(138,98,49,0.05)]' : '')}
                style={{ gridTemplateColumns: grid, borderTop: '1px solid rgba(20,33,61,.07)', background: k % 2 ? '#fcfbf8' : '#fff' }}
              >
                {columns.map((c, j) => (
                  <div key={j} className={'min-w-0 ' + (c.right ? 'text-right' : '')} style={{ padding: '12px' }}>
                    {c.render(row, k)}
                  </div>
                ))}
              </div>
            ))
          :
            <div className='flex flex-col items-center justify-center text-center' style={{ borderTop: '1px solid rgba(20,33,61,.07)', padding: '48px 16px', gap: '8px' }}>
              <div style={{ fontSize: '13px', color: 'rgba(20,33,61,.5)' }}>{emptyText}</div>
              {emptyAction}
            </div>
          }
        </div>
      </div>
      {footer}
    </div>
  );
}

// common cell styles
export const cell = {
  index: { font: "400 12px/1 'IBM Plex Mono',monospace", color: 'rgba(20,33,61,.45)' },
  primary: { font: "500 13px/1.3 'IBM Plex Sans',sans-serif", color: C.navy },
  muted: { fontSize: '12.5px', color: 'rgba(20,33,61,.6)' },
  date: { fontSize: '12.5px', color: 'rgba(20,33,61,.7)' },
  num: (v) => ({ font: "400 12.5px/1 'IBM Plex Mono',monospace", color: v > 0 ? C.navy : 'rgba(20,33,61,.45)', fontWeight: v > 0 ? 500 : 400 }),
  empty: { fontSize: '12.5px', color: 'rgba(20,33,61,.3)' },
};

// ---------- pagination ----------
const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

export function Pagination({ page, totalPages, onPage }) {
  if (totalPages < 1) return null;
  return (
    <div className='flex flex-wrap items-center justify-between bg-white' style={{ borderTop: '1px solid rgba(20,33,61,.1)', padding: '14px 16px', gap: '10px' }}>
      <button
        type='button'
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className='flex items-center transition-colors enabled:hover:bg-[rgba(20,33,61,0.03)] disabled:cursor-not-allowed'
        style={{ gap: '6px', border: '1px solid rgba(20,33,61,.12)', padding: '8px 14px', borderRadius: '4px', fontSize: '12.5px', fontWeight: 500, color: page <= 1 ? 'rgba(20,33,61,.35)' : C.navy, background: '#fff' }}
      >
        <ChevronLeft size={14} strokeWidth={2} /> Previous
      </button>
      <div className='flex items-center flex-wrap justify-center' style={{ gap: '6px' }}>
        {pageList(page, totalPages).map((p, i) => (
          p === '…' ?
            <span key={'e' + i} style={{ width: '20px', textAlign: 'center', fontSize: '12.5px', color: 'rgba(20,33,61,.45)' }}>…</span>
            :
            <button
              key={p}
              type='button'
              onClick={() => onPage(p)}
              className='flex items-center justify-center transition-colors'
              style={p === page
                ? { width: '30px', height: '30px', borderRadius: '4px', background: C.navy, color: C.paper, fontSize: '12.5px', fontWeight: 600 }
                : { width: '30px', height: '30px', borderRadius: '4px', border: '1px solid rgba(20,33,61,.15)', color: 'rgba(20,33,61,.6)', fontSize: '12.5px', background: '#fff' }}
            >
              {p}
            </button>
        ))}
      </div>
      <button
        type='button'
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        className='flex items-center transition-colors bg-tan enabled:hover:bg-tanDark disabled:opacity-40 disabled:cursor-not-allowed'
        style={{ gap: '6px', padding: '8px 14px', borderRadius: '4px', fontSize: '12.5px', fontWeight: 500, color: C.paper }}
      >
        Next <ChevronRight size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
